import { Injectable } from '@angular/core';
import { PRODUCT_GROUPS, PRODUCT_GROUP_NAMES } from '../enums/product-groups.enum';
import { ProductGroupInfo } from '../interfaces/get-product-types.interfaces';
import { PlanServiceResponse } from '../interfaces/plan-service-response.interface';
import { MedicarePlansService } from './plan-service-strategies/medicare-plans.service';
import { PlanTypesCollection } from '../interfaces/plan-types-collection.interface';
import { RecommendedPlans } from '../interfaces/recommended-plans.interface';
import { IndividualPlansService } from './plan-service-strategies/individual-plans.service';
import { Observable, Subject } from 'rxjs';

export interface getPlansStrategy {
  planServiceUrl: string;
  createPayload: any;
  getPlans: <T>(payload) => Promise<PlanServiceResponse>,
  storePlans: any;
  storeRecommendedPlans?: any;
  postRetrievalHooks?: any;
}

@Injectable({
  providedIn: 'root'
})
export class RetrievePlansService {

  private plansRetrievedSubject: Subject<void> = new Subject<void>();
  plansRetrieved$: Observable<void> = this.plansRetrievedSubject.asObservable();

  planServiceUrl: string;

  strategy: getPlansStrategy;

  constructor(
    private medicarePlansService: MedicarePlansService,
    private individualPlansService: IndividualPlansService
  ) { }

  setPlanServiceUrl(url: string): void {
    this.planServiceUrl = url;
  }

  setStrategy(strategyName: PRODUCT_GROUP_NAMES): void {
    switch (strategyName) {
      case PRODUCT_GROUPS.MEDICARE_ADVANTAGE:
      case PRODUCT_GROUPS.PRESCRIPTION_DRUG_PLAN:
      case PRODUCT_GROUPS.MEDIGAP:
      case PRODUCT_GROUPS.MEDICARE_SUPPLEMENT:
      case PRODUCT_GROUPS.WHOLECARE:
      case PRODUCT_GROUPS.DSNP:
        this.strategy = this.medicarePlansService;
        break;
      case PRODUCT_GROUPS.OFF_EXCHANGE:
      case PRODUCT_GROUPS.DENTAL:
      case PRODUCT_GROUPS.BLUE_EDGE_BALANCE:
      case PRODUCT_GROUPS.ON_EXCHANGE:
        this.strategy = this.individualPlansService;
        // this.strategy = {};
        break;
      default:
        console.error('A valid Retrieve Plan Service strategy is required')
      // this.strategy = {}
    }
  }

  async getPlans(productGroups: ProductGroupInfo, planYear?: number, productTabs?: PRODUCT_GROUP_NAMES[]) {
    return this.getPlansService(productGroups, planYear, productTabs);
  }

  getPlansService(productGroups: ProductGroupInfo, planYear?: number, productTabs?: PRODUCT_GROUP_NAMES[]) {
    let combinedPlansResponse = {} as PlanTypesCollection;
    let recommendedPlans = {} as RecommendedPlans;
    let combinedRecommendedPlans: RecommendedPlans[] = [];

    return new Promise(async (resolve, reject) => {
      // Compile the list of ONLY the active product group keys to fetch plans only for what is needed
      let productGroupKeys = Object.keys(productGroups);
      if (productTabs?.length) {
        productGroupKeys = productGroupKeys.filter(productGroup => {
          return (productTabs as string[]).includes(productGroup);
        })
      }

      // Each product group has it's own specific payload and service, so we have to call a service for each unique product type
      // TODO - it would be nice if we could supply one payload and execute one service call for Medicare, currently we have
      // to use two separate calls, one for all MA products (including Medigap) and one to retrieve specific medigap products after the user provides their preauth details
      for (let productGroup of productGroupKeys) {
        const productIds = combinedPlansResponse?.[productGroup] && Object.keys(combinedPlansResponse[productGroup]);
        const allowEnroll = combinedPlansResponse?.[productGroup]?.[productIds[0]]?.allowEnroll;
        const hasProducts = productIds?.length > 0;

        if (!hasProducts || !allowEnroll) {
          this.setStrategy(productGroup as PRODUCT_GROUP_NAMES);
          const payload = this.strategy.createPayload(productGroup, planYear);
          const plansResponse: PlanServiceResponse = await this.strategy.getPlans(payload);

          if (plansResponse.recommendedPlans) {
            recommendedPlans = plansResponse.recommendedPlans;
            combinedRecommendedPlans.push(plansResponse.recommendedPlans);
            this.strategy.storeRecommendedPlans?.(recommendedPlans);
          }

          for (let plan in plansResponse.plans) {
            const planProductIds = combinedPlansResponse?.[plan] && Object.keys(combinedPlansResponse[plan]);
            const planAllowEnroll = combinedPlansResponse?.[plan]?.[planProductIds[0]]?.allowEnroll;
            if (!combinedPlansResponse[plan] || !planAllowEnroll) {
              combinedPlansResponse[plan] = plansResponse.plans[plan];
            }
          }
        }

        this.strategy.storePlans(combinedPlansResponse);
        this.plansRetrievedSubject.next();
        this.strategy.postRetrievalHooks(combinedPlansResponse, combinedRecommendedPlans);
      }

      resolve(combinedPlansResponse);
    });
  }
}
